import { useState, useRef, useCallback, useEffect } from 'react';
import { logger } from '@/lib/logger';

export interface AudioDevice {
  deviceId: string;
  label: string;
}

export interface VoiceRecordingState {
  isRecording: boolean;
  audioBlob: Blob | null;
  audioBase64: string | null;
  error: string | null;
  duration: number;
  silenceWarning: boolean;
  availableDevices: AudioDevice[];
  selectedDeviceId: string | null;
}

export const useVoiceRecording = () => {
  const [state, setState] = useState<VoiceRecordingState>({
    isRecording: false,
    audioBlob: null,
    audioBase64: null,
    error: null,
    duration: 0,
    silenceWarning: false,
    availableDevices: [],
    selectedDeviceId: null,
  });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<number | null>(null);
  const silenceDetectorRef = useRef<{
    interval: number;
    audioContext: AudioContext;
    hasDetectedSound: boolean;
  } | null>(null);

// Safe check for mediaDevices support
  const isMediaDevicesSupported = useCallback(() => {
    return typeof navigator !== 'undefined' && 
           navigator.mediaDevices && 
           typeof navigator.mediaDevices.getUserMedia === 'function';
  }, []);

  // Safe enumerate devices
  const safeEnumerateDevices = useCallback(async () => {
    if (
      typeof navigator === 'undefined' ||
      !navigator.mediaDevices ||
      !navigator.mediaDevices.enumerateDevices
    ) {
      logger.warn("useVoiceRecording", "enumerateDevices not supported");
      return [];
    }
    return navigator.mediaDevices.enumerateDevices();
  }, []);

  // Enumerate available audio input devices
  const refreshDevices = useCallback(async () => {
    if (!isMediaDevicesSupported()) {
      logger.warn("useVoiceRecording", "getUserMedia not supported on this platform");
      return;
    }
    
    try {
      // First request permission to get labeled devices
      await navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => stream.getTracks().forEach(track => track.stop()));
      
      const devices = await safeEnumerateDevices();
      const audioInputs = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`,
        }));
      
      
      setState(prev => ({
        ...prev,
        availableDevices: audioInputs,
        selectedDeviceId: prev.selectedDeviceId || audioInputs[0]?.deviceId || null,
      }));
    } catch (error) {
      logger.error("useVoiceRecording", "Error enumerating devices:", error);
    }
  }, [isMediaDevicesSupported, safeEnumerateDevices]);

  // Set selected device
  const setSelectedDevice = useCallback((deviceId: string) => {
    setState(prev => ({ ...prev, selectedDeviceId: deviceId }));
  }, []);

  // Listen for device changes - but DON'T request permission or refresh devices automatically
  // La permission sera demandée uniquement quand l'utilisateur clique sur "Commencer l'enregistrement"
  useEffect(() => {
    const handleDeviceChange = () => {
      // Only refresh if permission already granted AND we're currently recording
      const permission = localStorage.getItem('concretly_mic_permission');
      if (permission === 'granted' && state.isRecording) {
        refreshDevices();
      }
    };
    
    // Safe addEventListener - not supported in all environments (iOS WKWebView)
    if (
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices &&
      'addEventListener' in navigator.mediaDevices
    ) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
      return () => {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      };
    } else {
      logger.warn("useVoiceRecording", "addEventListener not supported on this platform");
      return;
    }
  }, [refreshDevices, state.isRecording]);

  const startRecording = useCallback(async () => {
    try {
      // Reset state
      setState(prev => ({
        ...prev,
        isRecording: false,
        audioBlob: null,
        audioBase64: null,
        error: null,
        duration: 0,
        silenceWarning: false,
      }));
      chunksRef.current = [];

      // Request microphone access with selected device or default
      const audioConstraints: MediaTrackConstraints | boolean = state.selectedDeviceId
        ? { deviceId: { exact: state.selectedDeviceId } }
        : true;
      
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });
      
      // Permission granted - save for future sessions
      localStorage.setItem('concretly_mic_permission', 'granted');
      
      // Wait a bit for the track to initialize
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Log audio track settings for debugging
      const audioTrack = stream.getAudioTracks()[0];
      const settings = audioTrack?.getSettings();

      // Create MediaRecorder with best available format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { 
        mimeType,
        audioBitsPerSecond: 128000 // 128kbps for better quality
      });
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // Clear duration interval
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = null;
        }

        // Clean up silence detector and check if sound was detected
        let detectedSound = false;
        if (silenceDetectorRef.current) {
          clearInterval(silenceDetectorRef.current.interval);
          silenceDetectorRef.current.audioContext.close();
          detectedSound = silenceDetectorRef.current.hasDetectedSound;
          silenceDetectorRef.current = null;
        }
        
        if (!detectedSound) {
          logger.warn("useVoiceRecording", "No sound was detected during recording - audio may be silent");
        }

        // Log chunks for debugging
        const totalSize = chunksRef.current.reduce((sum, c) => sum + c.size, 0);

        // Create blob from chunks
        const blob = new Blob(chunksRef.current, { type: mimeType });
        
        // Set silence warning if no sound was detected
        if (!detectedSound) {
          setState(prev => ({ ...prev, silenceWarning: true }));
        }
        
        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          setState(prev => ({
            ...prev,
            isRecording: false,
            audioBlob: blob,
            audioBase64: base64,
          }));
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorder.onerror = (event) => {
        logger.error("useVoiceRecording", "MediaRecorder error:", event);
        setState(prev => ({
          ...prev,
          isRecording: false,
          error: 'Erreur lors de l\'enregistrement',
        }));
      };

      // Start recording WITHOUT timeslice - collect all data at once on stop
      // Using timeslice causes small/empty chunks issues in many browsers
      mediaRecorder.start();
      startTimeRef.current = Date.now();

      // Set up silence detection AFTER MediaRecorder is started (non-interfering)
      try {
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        // Don't connect to destination to avoid echo
        
        silenceDetectorRef.current = {
          interval: 0,
          audioContext,
          hasDetectedSound: false,
        };
        
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        silenceDetectorRef.current.interval = window.setInterval(() => {
          analyser.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          if (avg > 5 && silenceDetectorRef.current) {
            silenceDetectorRef.current.hasDetectedSound = true;
          }
        }, 500);
      } catch (e) {
        logger.warn("useVoiceRecording", "Could not set up silence detection:", e);
      }

      // Update duration every 100ms
      durationIntervalRef.current = window.setInterval(() => {
        setState(prev => ({
          ...prev,
          duration: Math.floor((Date.now() - startTimeRef.current) / 1000),
        }));
      }, 100);

      setState(prev => ({
        ...prev,
        isRecording: true,
        error: null,
      }));

    } catch (error) {
      logger.error("useVoiceRecording", "Error starting recording:", error);
      
      // Save denied permission status
      if (error instanceof Error && error.name === 'NotAllowedError') {
        localStorage.setItem('concretly_mic_permission', 'denied');
      }
      
      setState(prev => ({
        ...prev,
        isRecording: false,
        error: error instanceof Error 
          ? error.name === 'NotAllowedError'
            ? 'Accès au microphone refusé. Veuillez autoriser l\'accès.'
            : error.message
          : 'Erreur lors de l\'accès au microphone',
      }));
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      // Force collection of last chunk before stopping (important for Safari)
      if (mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.requestData();
        // Small delay to ensure data is collected before stop
        setTimeout(() => {
          mediaRecorderRef.current?.stop();
        }, 100);
      }
    }
  }, [state.isRecording]);

  const resetRecording = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (silenceDetectorRef.current) {
      clearInterval(silenceDetectorRef.current.interval);
      silenceDetectorRef.current.audioContext.close();
      silenceDetectorRef.current = null;
    }
    
    setState(prev => ({
      ...prev,
      isRecording: false,
      audioBlob: null,
      audioBase64: null,
      error: null,
      duration: 0,
      silenceWarning: false,
    }));
    chunksRef.current = [];
  }, []);

  return {
    ...state,
    startRecording,
    stopRecording,
    resetRecording,
    refreshDevices,
    setSelectedDevice,
  };
};
